import { useState } from 'react';

import { useMyIdentity } from '@/hooks/api/useDashboard';
import styles from './NpiNotice.module.css';

/**
 * Slim warning banner shown under the header when the signed-in user has no
 * provider NPI configured (no Entra `extn.npi` claim and no
 * provider_identity row). Without an NPI the dashboard cannot personalize
 * scores, rank, or trends, so we say so instead of silently showing "--".
 *
 * Renders nothing while identity is loading, on identity-endpoint errors
 * (never block the dashboard over the notice), when the user is properly
 * scoped, or after the user dismisses it for the session.
 */
export function NpiNotice() {
  const { data, isSuccess } = useMyIdentity();
  const [dismissed, setDismissed] = useState(false);

  if (!isSuccess || dismissed || data.providerScoped) return null;

  return (
    <div className={styles.banner} role="status">
      <svg
        className={styles.icon}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      <div className={styles.copy}>
        <strong className={styles.title}>Your NPI isn&apos;t configured</strong>
        <span className={styles.detail}>
          Scores, rank, and trends can&apos;t be personalized until your account is linked to your
          provider NPI. Please contact your administrator.
        </span>
      </div>
      <button
        type="button"
        className={styles.dismiss}
        onClick={() => setDismissed(true)}
        aria-label="Dismiss NPI notice"
      >
        ×
      </button>
    </div>
  );
}
